import api from '@crm-src/lib/api'

export interface CustomerActivity {
  id: string
  type: string
  date: string
  description: string
  details?: {
    status?: string
    duration?: string
    amount?: number
    notes?: string
    priority?: string
    due_date?: string
    completed_at?: string
    overdue?: boolean
    result?: string
    direction?: string
    opened?: boolean
    clicked?: boolean
    replied?: boolean
    location?: string
  }
  author?: string
  assignee?: string
  created_at: string
  updated_at: string
}

export interface ScheduleActivityData {
  title: string
  description?: string
  activity_type: 'call' | 'email' | 'meeting' | 'task' | 'note' | 'demo' | 'follow_up' | 'deal_renewal'
  due_date: string
  task_priority?: 'low' | 'medium' | 'high' | 'urgent'
  assigned_to_id?: number
  customer_deal_id?: number
  duration_minutes?: number
  location?: string
  status?: 'scheduled' | 'in_progress' | 'completed'
}

export const customerActivitiesApi = {
  /**
   * Get activities for a specific customer
   */
  getActivities: async (customerId: string, params?: {
    type?: string
    status?: string
    limit?: number
    page?: number
    per_page?: number
    search?: string
  }): Promise<{
    activities: CustomerActivity[]
    count: number
    total_activities: number
    activity_summary: Record<string, number>
    customer: { 
      id: string
      name: string
      type: string
      created_at: string
    }
  }> => {
    const query = new URLSearchParams()
    if (params?.type) query.append('type', params.type)
    if (params?.status) query.append('status', params.status)
    if (params?.limit) query.append('limit', params.limit.toString())
    if (params?.page) query.append('page', params.page.toString())
    if (params?.per_page) query.append('per_page', params.per_page.toString())
    if (params?.search) query.append('search', params.search)

    const url = `/customers/${customerId}/activities?${query.toString()}`
    
    try {
      const response = await api.get(url)
      
      // Validate response structure
      if (!response.data) {
        throw new Error('Invalid response: missing data property')
      }
      
      // Transform the backend response to match the frontend interface
      return {
        activities: response.data.data || [],
        count: response.data.meta?.pagination?.total_count || response.data.meta?.count || 0,
        total_activities: response.data.meta?.pagination?.total_count || response.data.meta?.total_activities || 0,
        activity_summary: response.data.meta?.activity_summary || {},
        customer: response.data.meta?.customer || {
          id: customerId,
          name: 'Unknown',
          type: 'Customer',
          created_at: new Date().toISOString()
        }
      }
    } catch (error: any) {
      // Log detailed error information
      console.error('Error fetching customer activities:', {
        customerId,
        url,
        error: error?.response?.data || error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText
      })
      
      // Re-throw with enhanced error message
      if (error?.response?.data?.error) {
        throw new Error(error.response.data.error)
      } else if (error?.response?.data?.message) {
        throw new Error(error.response.data.message)
      } else if (error?.response?.status === 404) {
        throw new Error('Customer not found or access denied')
      } else if (error?.response?.status === 403) {
        throw new Error('You do not have permission to view this customer\'s activities')
      } else if (error?.response?.status === 401) {
        throw new Error('Authentication required. Please log in again.')
      } else {
        throw error
      }
    }
  },

}

export default customerActivitiesApi
